import type {Readable, Writable} from 'svelte/store'
import type {IProduct, IShow, IShowValues} from '../types/Show'
import {ShowType, ShowState} from '../types/Show'
import {dispatchPresenterChanged} from '../global-events'
import fetchWithTimeout from '../../../lib/fetchWithTimeout'
import displayPrice from '../../../lib/displayPrice'
import translate from '../../../lib/translate' 
import {extractProducts, extractProductVariantAspectsAvailability} from '../../../lib/extractProducts'
import label from './ShowController.translation'
import {
    showStore,
    valuesStore,
    currentProductIdStore,
    productsStore,
    nextProductsStore,
    lastProductsStore,
    showMessageStore,
    IShowMessageStore, ILastProductsStore
} from '../stores'
import {get} from "svelte/store";

export class ShowController {
    
    // stores and data
    
    show: IShow
    showStore: Writable<IShow>
    
    values: IShowValues|null
    valuesStore: Writable<IShowValues|null>
    
    currentProductId: string|null
    currentProductIdStore: Writable<string|null>
    
    products: Array<IProduct>
    productsStore: Writable<Array<IProduct>>
    
    nextProducts: Array<IProduct>
    nextProductsStore: Writable<Array<IProduct>>
    lastProductsStore: ILastProductsStore

    showMessageStore: IShowMessageStore
    
    // internals
    
    statusUrl: string
    configUrl: string
    
    loopTimeout: number
    loopEntered: Date
    readonly loopTimeMs = 1000
    loopFetchConfig = true
    
    lastShowId: number
    lastShowState: ShowState
    lastFetch: Date
    
    // maps

    readonly mapStates = {
        'running' : ShowState.Running,
        'ended' : ShowState.Ending,
        'cancelled' : ShowState.Cancelled,
    }
    readonly mapTypes = {
        'singleVariant' : ShowType.SingleVariant,
        'multiVariant' : ShowType.MultiVariant,
        'ondemand' : ShowType.SingleCreation,
        'multiCreation' : ShowType.MultiCreation,
        'multiProduct' : ShowType.MultiProduct,
    }
    
    constructor(
        showStore: Writable<IShow>,
        valuesStore: Writable<IShowValues|null>,
        productsStore: Writable<Array<IProduct>>,
        nextProductsStore: Writable<Array<IProduct>>,
        lastProductsStore: ILastProductsStore,
        currentProductIdStore: Writable<string|null>,
        showMessageStore: IShowMessageStore,
    ) {
        // initial store values
        this.show = {
            canBid: false,
            state: ShowState.Init,
        }
        this.values = null
        this.currentProductId = null
        this.products = []
        this.nextProducts = []
        
        // initial internal state
        this.lastShowState = ShowState.Init
        this.lastShowId = null
        this.lastFetch = new Date

        // urls
        this.statusUrl = 'https://api.juwelo.de/api/no-auth/app/channel-game/status/'+globalThis.locale.gameChannel
        this.configUrl = 'https://api.juwelo.de/api/no-auth/app/channel-game/config/'+globalThis.locale.language+'/'+globalThis.locale.gameChannel
        
        // init stores and make setter accessible in class
        this.showStore = showStore
        this.valuesStore = valuesStore
        this.productsStore = productsStore
        this.nextProductsStore = nextProductsStore
        this.lastProductsStore = lastProductsStore
        this.currentProductIdStore = currentProductIdStore
        this.showMessageStore = showMessageStore
    }
    
    // controls
    
    selectCurrentProduct(productId: string) {
        if( this.products.length > 0 && 
            (
                !productId
                    || 
                this.products.filter(product => product.productId === productId).length === 0
            )
        ) {
            productId = this.products[0].productId
        }
        if(this.currentProductId !== productId) {
            this.currentProductId = productId
            this.currentProductIdStore.set(this.currentProductId)
        }
    }
    
    // fetching
    
    async fetch(url: string, timeoutMs: number) {
        try {
            return await fetchWithTimeout(url, {
                timeout: timeoutMs
            }).then(result => result.json())
        } catch(error) {
            console.error(error)
            return null;
        }
    }
    
    async fetchConfig() {
        return this.fetch(this.configUrl, 5000).then(data => {
            if(data) {
                this.updateShow(data.stateInformation, data.initialGameData)
                this.updateValues(data.stateInformation)
                this.updateProducts(data.stateInformation, data.playedProducts)
                this.updateNextProducts(data.upcomingGames)
                this.lastFetch = new Date()
            }
        })
    }

    async fetchStatus() {
        return this.fetch(this.statusUrl, 2500).then(data => {
            if(data) {
                this.updateShow(data)
                this.updateValues(data)
                this.updateProducts(data, [])
                this.updateVariants(data)
                this.lastFetch = new Date()
            }
        })
    }
    
    // value updates
    
    updateShow(stateInformation, initialGameData = null) {
        let before = JSON.stringify(this.show)
        
        let showStateBefore = this.show.state
        
        this.show.state = this.mapStates[stateInformation.state] || ShowState.Break 
        this.show.canBid = stateInformation.bidStatus === 'enabled'
        
        let showStateAfter = this.show.state
        
        if(!this.isProductShowState(this.show.state)) {
            
            delete this.show.meta
            
        } else if(stateInformation.gameId && initialGameData) {
            
            this.show.meta = {
                id: stateInformation.gameId,
                startPrice: {
                    cent: initialGameData.startPrice,
                    text: displayPrice(initialGameData.startPrice),
                },
                type: this.mapTypes[initialGameData.gameType],
                presenter: initialGameData.presenter,
                presenterId: initialGameData.presenterKey,
            }
            
            dispatchPresenterChanged(initialGameData.presenter, initialGameData.presenterKey)
            
        } else if(this.show.meta && stateInformation.gameId) {
            
            this.show.meta.id = stateInformation.gameId
            
        }
        
        if(get(this.showMessageStore) === null) {
            if(showStateAfter === ShowState.Ending && this.values?.currentPrice) {
                this.showMessageStore.set({
                    text: translate(label.endingMessage(this.values.currentPrice)),
                    durationMs: null,
                    priority: 3
                })
            }  
            if(showStateAfter === ShowState.Cancelled) {
                this.showMessageStore.set({
                    text: translate(label.cancelledMessage),
                    durationMs: null,
                    priority: 3
                })
            }
        } else if( 
            showStateAfter !== ShowState.Cancelled && 
            showStateAfter !== ShowState.Ending
        ) {
            // clear all messages when new show is running and store is not empty
            this.showMessageStore.set(null)
        }
        
        let after = JSON.stringify(this.show)
        if(after !== before) {
            this.showStore.set(this.show)
        }
    }
    
    updateValues(stateInformation) {
        let before = JSON.stringify(this.values)
        
        if(stateInformation.currentPrice !== undefined && stateInformation.currentQuantity !== undefined) {
            this.values = {
                currentPrice: {
                    cent: stateInformation.currentPrice,
                    text: displayPrice(stateInformation.currentPrice),
                },
                quantity: stateInformation.currentQuantity
            }
        } else {
            this.values = null
        }
        
        let after = JSON.stringify(this.values)        
        if(after !== before) {
            this.valuesStore.set(this.values)
            //console.log({values: this.values})
        }
    }
    
    updateProducts(stateInformation, playedProducts: Array<Object> = []) {
        let before = JSON.stringify(this.products)
        
        const lastProducts = this.products
        
        if(this.isProductShowState(this.show.state)) {
            if(playedProducts.length > 0 && stateInformation.variantMap?.length > 0) {
                // only update if data is available, otherwise leave alone
                this.products = extractProducts(playedProducts, stateInformation.variantMap)
            }
        } else {
            // empty when indicated by state
            this.products = []
        }

        let after = JSON.stringify(this.products)
        if(after !== before) {
            this.productsStore.set(this.products)
            this.selectCurrentProduct(this.products[0]?.productId)
            
            // also add to last products
            lastProducts.reverse()
            lastProducts.map((lastProduct) => this.lastProductsStore.add(lastProduct))
        }
    }
    
    updateVariants(stateInformation) {
        
        if(this.products.length === 0) {
            return
        }
        if(!stateInformation.variantMap) {
            return
        }
        
        let variantStatusChanged = false
                
        for(let i=0;i<stateInformation.variantMap.length;i++) {
            let variantData = stateInformation.variantMap[i]
            let p = this.products.map(product => product.productId).indexOf(variantData.productId)
            if(p < 0) {
                continue
            }
            let product = this.products[p]
            let v = product.variants.map(variant => variant.variantId).indexOf(variantData.variantId)
            let variant = product.variants[v]

            let isVariantEnabled = variantData.status === 'enabled'
            if(variant.available !== isVariantEnabled) {
                variantStatusChanged = true
                variant.available = isVariantEnabled
            }
        }

        if(!variantStatusChanged) {
            return
        }

        for(let i=0;i<this.products.length;i++) {
            let product = this.products[i]

            // enable/disable product if any/all variants are enabled/disabled
            product.available = product.variants.filter(variant => variant.available).length > 0

            // update availableVariantAspects based on variantAvailability

            extractProductVariantAspectsAvailability(product)
        }

        this.productsStore.set(this.products)
        
        //console.log({products: this.products})
    }
    
    updateNextProducts(upcomingGames: Array<Object> = []) {
        let before = JSON.stringify(this.nextProducts)
        
        let nextProducts = []
        
        for(let i=0;i<upcomingGames.length;i++) {
            let upcomingGameData = upcomingGames[i]
            let upcomingGameProducts = extractProducts(upcomingGameData.products)
            nextProducts = [...nextProducts, ...upcomingGameProducts]
        }
        
        this.nextProducts = nextProducts
        
        let after = JSON.stringify(this.nextProducts)
        if(before !== after) {
            this.nextProductsStore.set(this.nextProducts)
        }
    }
    
    isProductShowState(state: ShowState): boolean {
        return state === ShowState.Running ||
            state === ShowState.Cancelled ||
            state === ShowState.Ending
    }
    
    // loop
    
    loop() {
        this.loopEntered = new Date
        try {
            if(this.loopFetchConfig) {
                this.fetchConfig().finally(() => { this.loopEnd() })
            } else {
                this.fetchStatus().finally(() => { this.loopEnd() })
            }
        } catch(error) {
            console.error(error)
        }
    }
    
    loopEnd() {
        // fetch the config when we transition: 
        // 1) to any state that requires full product data, from any state that does not
        // 2) show.id is set and suddenly changes (so we probably missed something)
        this.loopFetchConfig = (
            !this.isProductShowState(this.lastShowState) && 
            this.isProductShowState(this.show.state)
                ||
            this.show.meta && this.lastShowId !== this.show.meta.id
        )
        this.lastShowState = this.show.state
        this.lastShowId = this.show.meta?.id
        
        let timeNowMs = (new Date).getTime()
        let dateAge = timeNowMs-this.lastFetch.getTime()
        if(dateAge > 5000) {
            if(this.show.state !== ShowState.ConnectionError) {
                this.show.state = ShowState.ConnectionError
                this.showStore.set(this.show)
            }
            //console.log({dateAge: dateAge})
        }
        
        if(this.loopFetchConfig) {
            //console.log({nextFetch: 'config'})
            this.queue(0)
            return
        }
        
        // queue next standard loop in 1s minus time taken to process the loop
        
        let loopTimeTakenMs = timeNowMs-this.loopEntered.getTime()
        let nextDelayMs = Math.max(0, this.loopTimeMs-loopTimeTakenMs)
        
        this.queue(nextDelayMs)
    }

    queue(delayMs: number) {
        let store = this
        this.loopTimeout = window.setTimeout(() => { store.loop() }, delayMs)
    }
    
    // start/stop
    
    start() {
        this.stop()
        this.queue(this.loopTimeMs)
    }
    
    stop() {
        if(this.loopTimeout) {
            window.clearTimeout(this.loopTimeout)
        }
    }
}

let singleton: ShowController|null = null

export default (): ShowController => {
    if(!singleton) {
        singleton = new ShowController(
            showStore,
            valuesStore,
            productsStore,
            nextProductsStore,
            lastProductsStore,
            currentProductIdStore,
            showMessageStore,
        )
    }
    return singleton
}